import { ref, readonly } from 'vue';
import { isElectron } from '../config/runtime';
import { useAuthStore } from '../stores/auth';
import { getI18n } from '../locales';

/**
 * Background hub-appliance update notifier (Electron).
 *
 * Periodically calls the hub's `/system/updates/check` (the same endpoint the
 * Settings → Appliance update card uses) and, when a newer hub release is
 * available, pops a native notification balloon (or an in-app toast fallback)
 * — once per version. Deliberately separate from `useElectronUpdates`, which
 * tracks the desktop app binary (electron-updater), not the hub release.
 */

const CHECK_INTERVAL_MS = 60 * 60 * 1000; // hourly
const STORAGE_PREFIX = 'otto_hub_update_notified_';

const updateAvailable = ref(false);
const latestVersion = ref<string | null>(null);
const lastCheckAt = ref<number | null>(null);

let timer: number | null = null;
let started = false;

function notifiedKey(version: string): string {
  return `${STORAGE_PREFIX}${version}`;
}

function alreadyNotified(version: string): boolean {
  try {
    return localStorage.getItem(notifiedKey(version)) === '1';
  } catch {
    return false;
  }
}

function markNotified(version: string): void {
  try {
    localStorage.setItem(notifiedKey(version), '1');
  } catch {
    /* ignore */
  }
}

/**
 * Fetch the hub update status. `/system/updates/check` is admin-only, so
 * non-admins (who cannot install anyway) are silently skipped. Returns null
 * when not connected to a hub or the check fails.
 */
async function fetchHubUpdate(): Promise<{
  available: boolean;
  currentVersion?: string;
  latestVersion?: string;
} | null> {
  const authStore = useAuthStore();
  if (!authStore.isAuthenticated || !authStore.isAdmin) return null;
  try {
    const { apiFetch } = await import('../services/api');
    const info = await apiFetch<{
      available?: boolean;
      currentVersion?: string;
      latestVersion?: string;
    }>('/system/updates/check');
    if (!info) return null;
    const available = Boolean(info.available && info.latestVersion);
    updateAvailable.value = available;
    latestVersion.value = info.latestVersion ?? null;
    lastCheckAt.value = Date.now();
    return {
      available,
      currentVersion: info.currentVersion,
      latestVersion: info.latestVersion,
    };
  } catch {
    return null;
  }
}

async function notifyHubUpdate(version: string): Promise<void> {
  const gt = getI18n().global.t.bind(getI18n().global);
  const title = gt('settings.settingsUpdates.hubUpdateTitle', 'Hub update available');
  const message = gt(
    'settings.settingsUpdates.hubUpdateBody',
    // Named values belong in the *second* argument: with a default message first,
    // vue-i18n reads the third one as TranslateOptions, so `{version}` interpolated
    // empty and the toast painted "v." instead of the actual version.
    { version },
    'A new hub update is available: v{version}. Open Settings → System → Appliance update to install it.',
  );

  // Native balloon in Electron; in-app toast fallback elsewhere.
  const electron = window.electron;
  if (isElectron() && electron?.notifyUser) {
    try {
      await electron.notifyUser(title, message);
      return;
    } catch {
      // Fall through to the toast fallback.
    }
  }
  const { useToastStore } = await import('../stores/toast');
  useToastStore().addToast({ type: 'info', message, duration: 8000 });
}

async function runCheck(notify: boolean): Promise<void> {
  const result = await fetchHubUpdate();
  if (!result?.available || !result.latestVersion) return;
  if (!notify) return;
  if (alreadyNotified(result.latestVersion)) return;
  markNotified(result.latestVersion);
  await notifyHubUpdate(result.latestVersion);
}

function onVisibility(): void {
  if (!document.hidden) {
    void runCheck(true);
  }
}

export function useHubUpdateNotifier() {
  function start(intervalMs: number = CHECK_INTERVAL_MS): void {
    if (started) return;
    started = true;

    // Check immediately at app start — but the hub connection may land a moment
    // later, so also re-check once shortly after startup and on visibility
    // change (returning to the window).
    void runCheck(true);
    window.setTimeout(() => {
      void runCheck(true);
    }, 20_000);
    timer = window.setInterval(() => {
      void runCheck(true);
    }, intervalMs);
    document.addEventListener('visibilitychange', onVisibility);
  }

  function stop(): void {
    if (timer !== null) {
      window.clearInterval(timer);
      timer = null;
    }
    document.removeEventListener('visibilitychange', onVisibility);
    started = false;
  }

  return {
    start,
    stop,
    updateAvailable: readonly(updateAvailable),
    latestVersion: readonly(latestVersion),
    lastCheckAt: readonly(lastCheckAt),
  };
}
